import {
  AgendaSlotReservationApi,
  CreateAgendaSlotBulkApiPayload,
  SlotReservationDoctorSubstitute,
} from '@omecare/utils';
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import http from '@/utils/http.service';
import {
  AgendaSlotItemReservation,
  GetAgendaSlotItemResponse,
  getAgendaSlotsSchema,
} from '../queries/use-get-office-agendas-slots';

export type OpenOfficeAgendaSlotBulkResponse = {
  page: number;
  per_page: number;
  page_count: number;
  data: GetAgendaSlotItemResponse[];
};

const openOfficeAgendaSlotBulk = (payload: CreateAgendaSlotBulkApiPayload) => {
  return http.agendaSlots
    .createBulk(payload)
    .then<OpenOfficeAgendaSlotBulkResponse>(({ response, transform }) => {
      const result = transform.handle({
        response,
        attributes: {
          startAt: true,
          endAt: true,
          periodOfTheDay: true,
          type: true,
          rrule: true,
          exdate: true,
          retrocessionRate: true,
          withAssistant: true,
          forceWithoutAssistant: true,
          sourceId: true,
          occurenceIndex: true,
          morningDoctolibDataCreatedAt: true,
          isFeeApplicable: true,
          workAtTheSameTime: true,
          createdAt: true,
          updatedAt: true,
          pendingRequestCount: true,
        },
        relations: {
          doctorAttending: true,
          reservation: ({
            entity,
          }: {
            entity: AgendaSlotReservationApi;
          }): AgendaSlotItemReservation => {
            const doctorSubstitute =
              transform.getApiEntityRelation<SlotReservationDoctorSubstitute>({
                relation: 'doctorSubstitute',
                relationships: entity.relationships,
                included: response.included,
              });

            return {
              id: entity.id,
              status: entity.attributes.status,
              doctorSubstitute: {
                id: doctorSubstitute!.id,
                firstname: doctorSubstitute!.attributes.firstname,
                lastname: doctorSubstitute!.attributes.lastname,
                picture: doctorSubstitute!.attributes.picture,
                installable: doctorSubstitute!.attributes.installable,
                hasPhysicalSiteAccess: false,
              },
            };
          },
        },
        schema: getAgendaSlotsSchema,
      });

      // Zod schema defaults fill in pendingRequestCount and reservations at parse time
      return result as unknown as OpenOfficeAgendaSlotBulkResponse;
    });
};

const useOpenOfficeAgendaSlotBulkMutation = (): UseMutationResult<
  OpenOfficeAgendaSlotBulkResponse,
  Error,
  CreateAgendaSlotBulkApiPayload
> => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: openOfficeAgendaSlotBulk,
    onSuccess() {
      toast.success(t('http.open-office-agenda-slot-bulk.success'));
    },
  });
};

export default useOpenOfficeAgendaSlotBulkMutation;
